import { useState } from 'react';
import OSS from 'ali-oss';
import { OssStsToken } from '@packages/global';
import { ossDirectUploadApi } from '../services/user/ossDirectUpload.api';
import { fileUploadApi } from '../services/user/fileUpload.api';

// 模块级缓存，跨组件实例共享
let cachedToken: OssStsToken | null = null;
let tokenExpiry: number = 0;

function isTokenValid(): boolean {
  return cachedToken !== null && (tokenExpiry - Date.now()) > 5 * 60 * 1000;
}

const MIME_TO_EXT: Record<string, string> = {
  'image/jpeg': '.jpg',
  'image/jpg': '.jpg',
  'image/png': '.png',
  'image/webp': '.webp',
  'image/gif': '.gif',
  'video/mp4': '.mp4',
  'video/avi': '.avi',
  'video/mov': '.mov',
  'video/wmv': '.wmv',
  'video/flv': '.flv',
  'video/webm': '.webm',
  'audio/mpeg': '.mp3',
  'audio/mp3': '.mp3',
  'audio/wav': '.wav',
  'audio/ogg': '.ogg',
  'audio/m4a': '.m4a',
  'audio/aac': '.aac',
};

export function generateOssPath(file: File): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');

  // 从文件名提取扩展名
  const lastDot = file.name.lastIndexOf('.');
  let ext = lastDot > 0 ? file.name.slice(lastDot).toLowerCase() : '';

  // 无法提取时从 MIME 推断
  if (!ext) {
    ext = MIME_TO_EXT[file.type] || '';
  }

  const uuid = crypto.randomUUID();
  return `video-agent/${year}/${month}-${day}/${uuid}${ext}`;
}

/** 上传渠道：直传 OSS 或经服务器代传 */
export type UploadChannel = 'direct' | 'server';

interface UseOssDirectUploadReturn {
  uploadFile: (file: File) => Promise<string>;
  uploading: boolean;
  progress: number;
  /** 最近一次成功上传所走的渠道；尚未上传时为 null */
  lastChannel: UploadChannel | null;
}

/**
 * 判断直传错误是否应该回退到服务器代传
 * - 网络层错误：CORS / TLS / DNS / 超时 / 连接被重置
 * - OSS 临时不可用：5xx
 * 排除：用户主动取消、明确的 4xx 业务错误（除 408/429）
 */
function shouldFallbackToServer(err: unknown): boolean {
  if (!err) return false;
  const e = err as any;

  // 用户主动取消
  if (e.name === 'cancel' || e.code === 'cancel' || e.name === 'AbortError') {
    return false;
  }

  // ali-oss SDK 的网络/连接错误
  const code: string | undefined = e.code || e.name;
  const networkCodes = new Set([
    'RequestError',
    'ConnectionTimeoutError',
    'ResponseTimeoutError',
    'NetworkingError',
    'TimeoutError',
  ]);
  if (code && networkCodes.has(code)) return true;

  // 浏览器 fetch / XHR 错误特征
  const message: string = String(e.message || '').toLowerCase();
  if (
    message.includes('failed to fetch') ||
    message.includes('network error') ||
    message.includes('load failed') ||
    message.includes('timeout') ||
    message.includes('cors')
  ) {
    return true;
  }

  // OSS 服务端 5xx
  const status: number | undefined = e.status || e.statusCode || e.response?.status;
  if (typeof status === 'number') {
    if (status >= 500) return true;
    if (status === 408 || status === 429) return true;
    return false; // 其他 4xx 是业务错误（鉴权、签名失效），不回退
  }

  // 没有 status 通常是断网 / TLS 错误，回退试一次
  return true;
}

export function useOssDirectUpload(): UseOssDirectUploadReturn {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [lastChannel, setLastChannel] = useState<UploadChannel | null>(null);

  const uploadViaDirect = async (file: File): Promise<string> => {
    // 获取或刷新 Token
    if (!isTokenValid()) {
      const token = await ossDirectUploadApi.getStsToken();
      cachedToken = token;
      tokenExpiry = new Date(token.expiration).getTime();
    }

    const token = cachedToken!;
    const ossPath = generateOssPath(file);

    const client = new OSS({
      region: token.region,
      accessKeyId: token.accessKeyId,
      accessKeySecret: token.accessKeySecret,
      stsToken: token.securityToken,
      bucket: token.bucket,
      secure: true,
    });

    await client.multipartUpload(ossPath, file, {
      headers: { 'x-oss-object-acl': 'public-read' },
      progress: (p: number) => {
        setProgress(Math.round(p * 100));
      },
    });

    return `https://${token.bucket}.${token.region}.aliyuncs.com/${ossPath}`;
  };

  const uploadViaServer = async (file: File): Promise<string> => {
    const result = await fileUploadApi.uploadAuto(file, (percent) => {
      setProgress(percent);
    });
    return result.url;
  };

  const uploadFile = async (file: File): Promise<string> => {
    setUploading(true);
    setProgress(0);

    try {
      try {
        const url = await uploadViaDirect(file);
        setProgress(100);
        setLastChannel('direct');
        return url;
      } catch (directErr) {
        if (!shouldFallbackToServer(directErr)) {
          throw directErr;
        }
        // 直传失败，回退到服务器代传
        // eslint-disable-next-line no-console
        console.warn('[useOssDirectUpload] 直传 OSS 失败，回退到服务器代传', directErr);

        // STS Token 也可能因鉴权或签名问题失效，清掉重新拉
        cachedToken = null;
        tokenExpiry = 0;

        setProgress(0);
        const url = await uploadViaServer(file);
        setProgress(100);
        setLastChannel('server');
        return url;
      }
    } finally {
      setUploading(false);
    }
  };

  return { uploadFile, uploading, progress, lastChannel };
}
